<script setup lang="ts">
import { useEmployee } from '@/composables/useEmployee';
import { usePlatform } from '@/composables/usePlatform';
import { useUserStore } from '@/stores/useUserStore';
import type { WorkingPeriod } from '@/types/employee';
import { getCurrentRestaurantTime } from '@/utils/time';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import Button from 'primevue/button';
import Calendar from 'primevue/calendar';
import Dialog from 'primevue/dialog';
import { useToast } from 'primevue/usetoast';
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

dayjs.extend(utc);
dayjs.extend(timezone);

interface Props {
  visible: boolean;
  workingPeriod?: WorkingPeriod | null;
  restaurantUserId: number;
  mode: 'create' | 'edit';
  presetDate?: Date | null; // New prop for preset date
}

const isMobilePhone = usePlatform().isMobilePhone;

interface Emits {
  (e: 'update:visible', value: boolean): void;
  (e: 'saved', workingPeriod: WorkingPeriod): void;
}

const props = withDefaults(defineProps<Props>(), {
  workingPeriod: null,
  presetDate: null,
});

const emit = defineEmits<Emits>();

const { t } = useI18n();
const toast = useToast();
const { createManualWorkingPeriod, updateWorkingPeriod, isLoading } = useEmployee();
const userStore = useUserStore();
const user = ref(userStore.user);

// Get restaurant timezone
const restaurantTimezone = computed(() => {
  return user.value?.active_restaurant?.timezone || 'UTC';
});

// Form state
const form = reactive({
  startTime: null as Date | null,
  endTime: null as Date | null,
  lastPeriodOfDay: false,
});

// Validation errors
const errors = ref<Record<string, string>>({});

// Computed properties
const dialogTitle = computed(() => {
  return props.mode === 'create' ? t('employee.timeclock.addEntry') : t('employee.timeclock.editEntry');
});

const isFormValid = computed(() => {
  return form.startTime && (!form.endTime || form.endTime > form.startTime);
});

// Initialize form based on mode and preset date
const initializeForm = () => {
  if (props.mode === 'edit' && props.workingPeriod) {
    // Edit mode - populate with existing data converted to restaurant timezone
    const period = props.workingPeriod;

    // Convert to restaurant timezone and then adjust for browser timezone offset
    const startInRestaurantTz = dayjs.utc(period.start_time).tz(restaurantTimezone.value);
    const endInRestaurantTz = period.end_time ? dayjs.utc(period.end_time).tz(restaurantTimezone.value) : null;

    // Create dates that will display correctly in the browser's timezone
    form.startTime = createTimezoneAdjustedDate(startInRestaurantTz);
    form.endTime = endInRestaurantTz ? createTimezoneAdjustedDate(endInRestaurantTz) : null;
    form.lastPeriodOfDay = period.last_period_of_day || false;
  } else {
    // Create mode - use preset date or current time
    const now = getCurrentRestaurantTime(restaurantTimezone.value);

    if (props.presetDate) {
      // Use preset date with current time - ensure preset date is treated as UTC
      const presetDateTime = dayjs.utc(props.presetDate).tz(restaurantTimezone.value);
      const adjustedPresetDateTime = presetDateTime.hour(now.hour()).minute(now.minute()).second(0).millisecond(0);

      form.startTime = createTimezoneAdjustedDate(adjustedPresetDateTime);
    } else {
      // Use current time
      form.startTime = createTimezoneAdjustedDate(now);
    }

    form.endTime = null;
    form.lastPeriodOfDay = false;
  }

  errors.value = {};
};

// Helper function to create a date that displays correctly in the browser
const createTimezoneAdjustedDate = (dayjsObj: dayjs.Dayjs): Date => {
  // Get the restaurant time as a string and parse it in the browser's local timezone
  // This "tricks" the browser into showing the restaurant time as if it were local time
  const restaurantTimeString = dayjsObj.format('YYYY-MM-DD HH:mm:ss');
  return new Date(restaurantTimeString);
};

// Watch for prop changes to reset form
watch(
  () => props.visible,
  (newVisible) => {
    if (newVisible) {
      initializeForm();
    }
  },
  { immediate: true },
);

// Watch for preset date changes
watch(
  () => props.presetDate,
  () => {
    if (props.visible && props.mode === 'create') {
      initializeForm();
    }
  },
);

// Watch for working period changes in edit mode
watch(
  () => props.workingPeriod,
  () => {
    if (props.visible && props.mode === 'edit') {
      initializeForm();
    }
  },
);

// Validation
const validateForm = (): boolean => {
  errors.value = {};

  if (!form.startTime) {
    errors.value.startTime = t('employee.timeclock.validation.startTimeRequired');
  }

  if (form.endTime && form.startTime && form.endTime <= form.startTime) {
    errors.value.endTime = t('employee.timeclock.validation.endTimeAfterStart');
  }

  // Check for reasonable duration (max 24 hours)
  if (form.startTime && form.endTime) {
    const diffHours = (form.endTime.getTime() - form.startTime.getTime()) / (1000 * 60 * 60);
    if (diffHours > 24) {
      errors.value.endTime = t('employee.timeclock.validation.maxDuration', { hours: 24 });
    }
  }

  return Object.keys(errors.value).length === 0;
};

// Convert form date to UTC for API
const convertToUTC = (date: Date): string => {
  // The date represents restaurant time but is displayed as local time
  // We need to get the local time representation and treat it as restaurant time
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const seconds = date.getSeconds().toString().padStart(2, '0');

  // Create the time string as if it were in restaurant timezone
  const restaurantTimeString = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;

  // Parse this string explicitly as restaurant timezone and convert to UTC
  const restaurantTime = dayjs.tz(restaurantTimeString, restaurantTimezone.value);
  return restaurantTime.utc().toISOString();
};

const handleSave = async () => {
  if (!validateForm()) {
    return;
  }

  try {
    const data = {
      start_time: convertToUTC(form.startTime!),
      end_time: form.endTime ? convertToUTC(form.endTime) : undefined,
      last_period_of_day: form.lastPeriodOfDay,
    };

    let result;
    if (props.mode === 'create') {
      result = await createManualWorkingPeriod(props.restaurantUserId, data);
    } else {
      result = await updateWorkingPeriod(props.restaurantUserId, props.workingPeriod!.id, data);
    }

    if (result.error) {
      // error is in error.details.error
      const errorMessage = result.error.details.error || t('common.unknownError');
      toast.add({
        severity: 'error',
        summary: t('common.error'),
        detail: errorMessage,
        life: 3000,
      });
      return;
    }

    if (result.data) {
      toast.add({
        severity: 'success',
        summary: t('common.success'),
        detail: props.mode === 'create' ? t('employee.timeclock.entryCreated') : t('employee.timeclock.entryUpdated'),
        life: 3000,
      });

      emit('saved', result.data);
      handleClose();
    }
  } catch (error) {
    console.error('Error saving working period:', error);
    toast.add({
      severity: 'error',
      summary: t('common.error'),
      detail: t('employee.timeclock.saveFailed'),
      life: 3000,
    });
  }
};

const handleClose = () => {
  emit('update:visible', false);
};

// Add refs for the calendar components
const startTimeCalendar = ref();
const endTimeCalendar = ref();

// Clear the time field
const clearTime = (field: 'startTime' | 'endTime') => {
  form[field] = null;

  // Close the calendar
  if (field === 'startTime' && startTimeCalendar.value) {
    startTimeCalendar.value.overlayVisible = false;
  } else if (field === 'endTime' && endTimeCalendar.value) {
    endTimeCalendar.value.overlayVisible = false;
  }

  validateForm();
};

// Validate and close calendar - set to current time if empty
const validateAndClose = (field: 'startTime' | 'endTime') => {
  // If field is empty, set to current time
  if (!form[field]) {
    const now = getCurrentRestaurantTime(restaurantTimezone.value);
    form[field] = createTimezoneAdjustedDate(now);
  }

  // Close the calendar
  if (field === 'startTime' && startTimeCalendar.value) {
    startTimeCalendar.value.overlayVisible = false;
  } else if (field === 'endTime' && endTimeCalendar.value) {
    endTimeCalendar.value.overlayVisible = false;
  }

  validateForm();
};

// Calculate duration for display
const durationInMinutes = computed(() => {
  if (!form.startTime || !form.endTime) return 0;
  return Math.round((form.endTime.getTime() - form.startTime.getTime()) / (1000 * 60));
});

// Format duration display
const formatDuration = (minutes: number): string => {
  if (minutes < 60) {
    return `${t('common.minutesShort', { count: minutes })}`;
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h${remainingMinutes.toString().padStart(2, '0')}`;
};
</script>

<template>
  <Dialog :visible="visible" :header="dialogTitle" modal class="w-[90vw] max-w-md" @update:visible="handleClose">
    <div class="flex flex-col gap-4">
      <!-- Preset Date Info -->
      <!-- <div v-if="presetDate && mode === 'create'" class="rounded bg-blue-50 p-3 text-sm">
        <div class="flex items-center gap-2">
          <i class="pi pi-calendar text-blue-600"></i>
          <span class="text-blue-800">
            {{ t('employee.timeclock.addingEntryForDate') }}:
            <strong>
              {{ dayjs(presetDate).tz(restaurantTimezone).format('dddd, MMMM D, YYYY') }}
            </strong>
          </span>
        </div>
      </div> -->

      <!-- Start Time -->
      <div class="flex flex-col gap-2">
        <label for="startTime" class="font-medium"> {{ t('employee.timeclock.startTime') }} * </label>
        <Calendar
          id="startTime"
          ref="startTimeCalendar"
          v-model="form.startTime"
          :placeholder="t('employee.timeclock.selectStartTime')"
          show-time
          hour-format="24"
          :class="{ 'p-invalid': errors.startTime }"
          date-format="dd.mm.yy"
          :show-seconds="false"
          :touch-u-i="isMobilePhone"
          @blur="validateForm"
        >
          <template #footer>
            <div class="flex justify-between p-2">
              <Button :label="t('common.clear')" size="small" severity="secondary" outlined @click="clearTime('startTime')" />
              <Button :label="t('common.validate')" size="small" @click="validateAndClose('startTime')" />
            </div>
          </template>
        </Calendar>
        <small v-if="errors.startTime" class="p-error">
          {{ errors.startTime }}
        </small>
      </div>

      <!-- End Time -->
      <div class="flex flex-col gap-2">
        <label for="endTime" class="font-medium">
          {{ t('employee.timeclock.endTime') }}
        </label>
        <Calendar
          id="endTime"
          ref="endTimeCalendar"
          v-model="form.endTime"
          :placeholder="t('employee.timeclock.selectEndTime')"
          show-time
          hour-format="24"
          :class="{ 'p-invalid': errors.endTime }"
          date-format="dd.mm.yy"
          :show-seconds="false"
          :touch-u-i="isMobilePhone"
          @blur="validateForm"
        >
          <template #footer>
            <div class="flex justify-between p-2">
              <Button :label="t('common.clear')" size="small" severity="secondary" outlined @click="clearTime('endTime')" />
              <Button :label="t('common.validate')" size="small" @click="validateAndClose('endTime')" />
            </div>
          </template>
        </Calendar>
        <small v-if="errors.endTime" class="p-error">
          {{ errors.endTime }}
        </small>
        <small class="text-gray-500">
          {{ t('employee.timeclock.leaveEmptyForOngoing') }}
        </small>
      </div>

      <!-- Last Period of Day -->
      <!-- <div class="flex flex-row items-center gap-2">
        <Checkbox id="lastPeriodOfDay" v-model="form.lastPeriodOfDay" binary />
        <label for="lastPeriodOfDay" class="font-medium">
          {{ t('employee.timeclock.lastPeriodOfDay') }}
        </label>
      </div> -->

      <!-- Duration Preview -->
      <div v-if="form.startTime && form.endTime" class="rounded border bg-gray-50 p-3">
        <div class="flex justify-between text-sm">
          <span>{{ t('employee.timeclock.duration') }}:</span>
          <span class="font-medium">
            {{ formatDuration(durationInMinutes) }}
          </span>
        </div>
      </div>

      <!-- Timezone info -->
      <div class="text-xs text-gray-600">{{ t('common.timezone') }}: {{ restaurantTimezone }}</div>
    </div>

    <template #footer>
      <div class="flex justify-end gap-2">
        <Button :label="t('common.cancel')" severity="secondary" outlined @click="handleClose" />
        <Button :label="t('common.save')" :loading="isLoading" :disabled="!isFormValid" @click="handleSave" />
      </div>
    </template>
  </Dialog>
</template>
